import { useEffect, useState } from "react";

import { ApiError, apiClient } from "../../api/client";
import type { LedgerEntry, LedgerEntryType, Season } from "../../api/types";
import { displayName, formatDateTime, prettyEntryType } from "../../lib/format";
import { signedAmount } from "../../lib/ledgerDisplay";

const entryTypes: LedgerEntryType[] = [
  "coin_grant",
  "gumball_spend",
  "manual_adjustment",
  "season_expiry",
  "season_carryover"
];

interface State {
  status: "loading" | "ready" | "error";
  seasons: Season[];
  selectedSeasonID: string;
  entries: LedgerEntry[];
  nextCursor?: string;
  filterType: LedgerEntryType | "";
  errorMessage: string | null;
  loadingMore: boolean;
}

const initial: State = {
  status: "loading",
  seasons: [],
  selectedSeasonID: "",
  entries: [],
  filterType: "",
  errorMessage: null,
  loadingMore: false
};

export function Ledger() {
  const [state, setState] = useState<State>(initial);

  useEffect(() => {
    let cancelled = false;
    setState({ ...initial });

    async function load() {
      try {
        const seasons = await apiClient.listSeasons({ limit: 50 });
        if (cancelled) {
          return;
        }
        const initialSeason = seasons.seasons.find((s) => s.status === "active") ?? seasons.seasons[0];
        if (initialSeason === undefined) {
          setState({
            status: "ready",
            seasons: seasons.seasons,
            selectedSeasonID: "",
            entries: [],
            errorMessage: null,
            filterType: "",
            loadingMore: false
          });
          return;
        }
        const ledger = await apiClient.listLedger({ seasonID: initialSeason.id, limit: 50 });
        if (cancelled) {
          return;
        }
        setState({
          status: "ready",
          seasons: seasons.seasons,
          selectedSeasonID: initialSeason.id,
          entries: ledger.entries,
          ...(ledger.next_cursor !== undefined ? { nextCursor: ledger.next_cursor } : {}),
          errorMessage: null,
          filterType: "",
          loadingMore: false
        });
      } catch (caught) {
        if (cancelled) {
          return;
        }
        setState({
          ...initial,
          status: "error",
          errorMessage: messageFor(caught)
        });
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function changeSeason(seasonID: string) {
    setState((previous) => ({ ...previous, status: "loading", selectedSeasonID: seasonID }));
    try {
      const ledger = await apiClient.listLedger({ seasonID, limit: 50 });
      setState((previous) => ({
        ...previous,
        status: "ready",
        entries: ledger.entries,
        ...(ledger.next_cursor !== undefined ? { nextCursor: ledger.next_cursor } : { nextCursor: undefined }),
        errorMessage: null
      }));
    } catch (caught) {
      setState((previous) => ({ ...previous, status: "error", errorMessage: messageFor(caught) }));
    }
  }

  async function loadMore() {
    if (state.nextCursor === undefined || state.selectedSeasonID === "") {
      return;
    }
    setState((previous) => ({ ...previous, loadingMore: true }));
    try {
      const ledger = await apiClient.listLedger({
        seasonID: state.selectedSeasonID,
        cursor: state.nextCursor,
        limit: 50
      });
      setState((previous) => ({
        ...previous,
        entries: [...previous.entries, ...ledger.entries],
        ...(ledger.next_cursor !== undefined ? { nextCursor: ledger.next_cursor } : { nextCursor: undefined }),
        loadingMore: false
      }));
    } catch (caught) {
      setState((previous) => ({
        ...previous,
        loadingMore: false,
        errorMessage: messageFor(caught)
      }));
    }
  }

  const filtered = state.filterType === ""
    ? state.entries
    : state.entries.filter((entry) => entry.entry_type === state.filterType);

  return (
    <div className="ledger-view">
      <div className="card">
        <div className="filter-row">
          <label>
            Season
            <select
              value={state.selectedSeasonID}
              onChange={(event) => void changeSeason(event.target.value)}
              disabled={state.status === "loading"}
            >
              {state.seasons.map((season) => (
                <option key={season.id} value={season.id}>
                  {season.name} · {season.status}
                </option>
              ))}
            </select>
          </label>
          <label>
            Type
            <select
              value={state.filterType}
              onChange={(event) =>
                setState((previous) => ({
                  ...previous,
                  filterType: event.target.value as State["filterType"]
                }))
              }
            >
              <option value="">All</option>
              {entryTypes.map((type) => (
                <option key={type} value={type}>
                  {prettyEntryType(type)}
                </option>
              ))}
            </select>
          </label>
        </div>

        {state.status === "loading" && <p className="loading">Loading ledger…</p>}
        {state.status === "error" && state.errorMessage !== null && (
          <p className="form-row__error">{state.errorMessage}</p>
        )}
        {state.status === "ready" && filtered.length === 0 && (
          <p className="muted">No ledger entries match the current filters.</p>
        )}
        {state.status === "ready" && filtered.length > 0 && (
          <table className="ledger-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Source</th>
                <th>Target</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((entry) => (
                <tr key={entry.id}>
                  <td>{formatDateTime(entry.created_at)}</td>
                  <td>{prettyEntryType(entry.entry_type)}</td>
                  <td>{signedAmount(entry)}</td>
                  <td>{displayName(null, entry.source_user_email ?? entry.actor_user_email)}</td>
                  <td>{displayName(null, entry.target_user_email)}</td>
                  <td>{entry.reason ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {state.nextCursor !== undefined && (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => void loadMore()}
            disabled={state.loadingMore}
          >
            {state.loadingMore ? "Loading…" : "Load more"}
          </button>
        )}
      </div>
    </div>
  );
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Could not load ledger.";
}
